import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import BackspaceOutlinedIcon from "@material-ui/icons/BackspaceOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PropTypes from "prop-types";

import Confirm from "../../Confirm";
import DefaultDataTable from "../../Shared/DefaultDataTable";
import Selector from "../../Shared/Selector";
import TabNavigationCtrlFilterPanel from "../../TabNavigationCtrlFilterPanel";

import ModalData from "./modal-data";
import ModalRemovePlant from "./modal-remove-plant";
import useStyles from "./styles";
import { setDataStorage, getDataStorage, removeDataStorage } from "../../../utils/filterManagementDataStore";

const getDataStorageBranch = () => getDataStorage('plant_config_checkSingleBranch');
const getDataStorageDocTab = () => getDataStorage('plant_config_doc_tab');

const PlantsTabComponent = ({
  plants,
  branches,
  reservationTypes,
  filter,
  setFilter,
  subRoutes,
  onDeleteItem,
  onAddItem,
  onUpdateItem,
  plantsError,
  clearPlantErrors,
  getNotificationsFromSchedConfigAsync
}) => {
  const classes = useStyles();
  const [deletingItem, setDeletingItem] = useState(null);
  const [editingItem, setEditingItem] = useState({});
  const [isOpenAddForm, setOpenAddForm] = useState(false);
  const [plantsFiltered, setPlantsFiltered] = useState([]);
  const [schedulingConflict, setSchedulingConflict] = useState(null);

  const [cnpjs, setCnpjs] = useState([]);

  const ConfigButton = (item) => (
    <IconButton 
      data-cy={`edit-${item.id}`}
      id={`edit-${item.id}`}
      onClick={() => handleEditItem(item)}>
      <SettingsOutlinedIcon />
    </IconButton>
  );

  const handleEditItem = (item) => {
    setEditingItem(item);
    setOpenAddForm(true);
  };

  const handleAddTurn = () => {
    setOpenAddForm(true);
  };

  const handleModalDeleteClose = (confirm) => {
    if (confirm) {
      onDeleteItem(deletingItem);
    }
    setDeletingItem(null);
  };

  const handleSaveData = (data) => {
    const plantData = {
      ...data,
      id_branch: filter.plant_branch ?? getDataStorageBranch()?.value
    };

    if (!plantData.id) {
      return onAddItem(plantData);
    } else {
      return onUpdateItem(plantData);
    }
  };

  const handleCloseModal = () => {
    setOpenAddForm(false);
    setDeletingItem(null);
    setEditingItem({});
  };

  const DeleteButton = (item) => (
    <IconButton
      data-cy={`delete-${item.id}`}
      id={`delete-${item.id}`}
      onClick={() => {
        setDeletingItem(item);
      }}
    >
      <BackspaceOutlinedIcon />
    </IconButton>
  );

  useEffect(() => {
    if (filter.documentNumber) {
      setPlantsFiltered(plants.filter(
        (plant) => plant.document_number === filter.documentNumber
      ));
    } else {
      setPlantsFiltered(plants);
    }
  }, [plants, filter.documentNumber]);

  useEffect(() => {
    const documentNumbers = [
      ...new Set(plants.map((plant) => plant.document_number)),
    ];

    setCnpjs(documentNumbers.map((documentNumber) => ({
      value: documentNumber,
      label: documentNumber,
    })));
  }, [plants]);

  useEffect(() => {
    if (!!plantsError) {
      setSchedulingConflict(plantsError);
    } else {
      setSchedulingConflict(null);
    }
  }, [plantsError, setSchedulingConflict])

  const getWarnningMessage = (
    <p className={classes.warnningMessage}>Ao excluir essa planta todos os dados relacionados também serão excluídos.</p>
  )

  const handleBranchFilterChange = (selectedBranch) => {
    removeDataStorage('plant_config_doc_tab');
    filter.documentNumber = null;
    setFilter({ ...filter, plant_branch: selectedBranch && selectedBranch.value });
  };

  const handleDocumentNumberFilterOnChange = (selectedDocumentNumber) => {
    setFilter({
      ...filter,
      documentNumber: selectedDocumentNumber && selectedDocumentNumber.value,
    });
  };

  const noDataMessage = () => {
    if (!filter.plant_branch && !getDataStorageBranch()) {
      return "Por favor, selecione uma filial.";
    }
    return "Não existem dados a serem apresentados.";
  };

  const columns = [
    { name: "Nome da Planta", selector: "name", sortable: true },
    { name: "Número BP", selector: "bp_number", sortable: true },
    {
      name: "CNPJ",
      maxWidth: "200px",
      selector: "document_number",
      sortable: true,
    },
    {
      name: "Tipo de Negócio",
      maxWidth: "250px",
      selector: "bussiness_type",
      sortable: true,
    },
    {
      name: "Editar",
      maxWidth: "30px",
      cell: (row) => <ConfigButton {...row} />,
    },
    {
      name: "Excluir",
      maxWidth: "30px",
      cell: (row) => <DeleteButton {...row} />,
    },
  ];

  const handleOnChange = (funcChange, valueStorage, nameStorage) => {
    funcChange(valueStorage)
    if (valueStorage !== null) {
      setDataStorage(nameStorage, valueStorage)
    } else {
      removeDataStorage(nameStorage)
    }
  }

  return (
    <>
      <TabNavigationCtrlFilterPanel
        data-cy="plant-filter-pane"
        id="plant-filter-pane"
        headerFilter={
          <Grid className={classes.filters}>
            <Grid className={classes.branchFilter}>
              <Selector
                options={branches}
                placeholder="Filtrar por filial"
                data-cy="plant-branch-filter"
                id="plant-branch-filter"
                isClearable
                search
                selected={filter.plant_branch ?? getDataStorageBranch()?.value}
                value={getDataStorageBranch()}
                onChange={(selectedBranch) =>
                  handleOnChange(
                    handleBranchFilterChange,
                    selectedBranch,
                    'plant_config_checkSingleBranch'
                  )
                }
              />
            </Grid>
            <Grid className={classes.documentNumbersFilter}>
              <Selector
                options={cnpjs}
                placeholder="Filtrar por CNPJ"
                data-cy="plant-filter-cnpj"
                id="plant-filter-cnpj"
                isClearable
                selected={filter.documentNumber || getDataStorageDocTab()?.value}
                disabled={!filter.plant_branch && !getDataStorageBranch()}
                value={getDataStorageDocTab()}
                onChange={(selectedDocumentNumber) =>
                  handleOnChange(
                    handleDocumentNumberFilterOnChange,
                    selectedDocumentNumber,
                    'plant_config_doc_tab'
                  )
                }

              />
            </Grid>
          </Grid>
        }
        controlAreaClassName={classes.controlAreaClass}
        controlArea={
          <Button
            color="primary"
            variant="contained"
            data-cy="plant-add-button"
            id="plant-add-button"
            autoFocus
            size="large"
            disabled={!filter.plant_branch && !getDataStorageBranch()}
            className={classes.addButton}
            onClick={() => handleAddTurn()}
          >
            Adicionar planta
          </Button>
        }
        subRoutes={subRoutes}
      >
        <DefaultDataTable
          data-cy="plant-table"
          id="plant-table"
          columns={columns}
          data={plantsFiltered}
          noDataMessage={noDataMessage()}
        />
      </TabNavigationCtrlFilterPanel>
      <Confirm
        isOpen={!!deletingItem}
        title="Confirmar exclusão?"
        message="Deseja excluir esse item da sua lista de plantas?"
        data-cy="plant-confirm-modal"
        id="plant-confirm-modal"
        subMessage={getWarnningMessage}
        okText="Sim"
        cancelText="Não"
        handleClose={handleModalDeleteClose}
      />
      <ModalData
        open={isOpenAddForm}
        editedData={editingItem}
        data-cy="plant-modal-data"
        id="plant-modal-data"
        reservationTypes={reservationTypes}
        handleClose={() => handleCloseModal()}
        handleSave={(data) => handleSaveData(data)}
      />
      <ModalRemovePlant
        data={schedulingConflict}
        data-cy="plant-remove-modal"
        id="plant-remove-modal"
        isOpen={!!schedulingConflict}
        closeReadingNotificationDialog={() => {
          setSchedulingConflict(null);
          clearPlantErrors(false);
          getNotificationsFromSchedConfigAsync();
        }}
      />
    </>
  );
};

PlantsTabComponent.propTypes = {
  plants: PropTypes.array.isRequired,
  reservationTypes: PropTypes.array.isRequired,
  branches: PropTypes.array.isRequired,
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  subRoutes: PropTypes.array.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onUpdateItem: PropTypes.func.isRequired,
  plantErrors: PropTypes.object.isRequired,
  clearPlantErrors: PropTypes.func.isRequired,
  getNotificationsFromSchedConfigAsync: PropTypes.func.isRequired
};

export default PlantsTabComponent;
